import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";

export interface AppVersionUsageRow {
    realName?: string | null;
    nstitutionName?: string | null;
    phone?: string | null;
    updatedTime?: string;
    /** 兼容早期 React 测试数据，真实 Vue 接口字段为 updatedTime。 */
    updateTime?: string;
}

export interface AppVersionUsageQuery {
    page: number;
    size: number;
    versionId: number;
    type: number;
    platform: 1 | 2;
    sortKey?: string;
    sortOrder?: string;
}

export interface AppVersionUsageDialogProps {
    appVersion?: { id?: number; type?: number };
    platform: "ios" | "android";
    open: boolean;
    onClose: () => void;
    loadUsage?: (query: AppVersionUsageQuery) => Promise<{ list?: AppVersionUsageRow[]; total?: number }>;
    exportUsage?: (
        query: Omit<AppVersionUsageQuery, "page" | "size"> & { page: number; size: number },
        filename: string
    ) => Promise<unknown>;
}

const defaultSort = { sortKey: "updated_time", sortOrder: "desc" };

/** 复用 Vue APP 版本用户使用明细接口。 */
export async function loadAppVersionUsage(
    query: AppVersionUsageQuery
): Promise<{ list?: AppVersionUsageRow[]; total?: number }> {
    const response = await request.service.get("/admin/app-version/usage-app", { params: query });
    return response.data;
}

/** 复用 Vue APP 版本用户使用明细导出接口。 */
export async function exportAppVersionUsage(query: AppVersionUsageQuery, filename: string): Promise<unknown> {
    const response = await request.service.get("/admin/app-version/export-usage", {
        params: query,
        responseType: "blob"
    });
    const url = window.URL.createObjectURL(response as unknown as Blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    return response;
}

function formatDate(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16).replace(/-/g, "/") : DEFAULT_VALUE;
}

/** 按 Vue dateFormat 规则生成本地时间导出文件名，避免 UTC 跨日偏差。 */
function formatLocalTimestamp(value: Date): string {
    const pad = (part: number) => String(part).padStart(2, "0");
    return `${value.getFullYear()}${pad(value.getMonth() + 1)}${pad(value.getDate())}${pad(value.getHours())}${pad(value.getMinutes())}${pad(value.getSeconds())}`;
}

/** APP 版本用户使用明细弹窗，保留 Vue 平台、分页和导出状态。 */
export function AppVersionUsageDialog({
    appVersion,
    platform,
    open,
    onClose,
    loadUsage = loadAppVersionUsage,
    exportUsage = exportAppVersionUsage
}: AppVersionUsageDialogProps) {
    const [rows, setRows] = useState<AppVersionUsageRow[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc" | undefined>("desc");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const platformCode = platform === "ios" ? 1 : 2;

    useEffect(() => {
        if (!open || appVersion?.id == null || appVersion.type == null) return;
        let disposed = false;
        const query: AppVersionUsageQuery = {
            page,
            // 旧 Vue 弹窗 ProTable 默认每页展示 20 条。
            size: 20,
            versionId: appVersion.id,
            type: appVersion.type,
            platform: platformCode,
            ...(sortOrder ? { sortKey: defaultSort.sortKey, sortOrder } : {})
        };
        void loadUsage(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("用户使用明细加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [appVersion?.id, appVersion?.type, loadUsage, open, page, platformCode, sortOrder]);

    const onExport = async () => {
        if (appVersion?.id == null || appVersion.type == null) {
            setErrorMessage("请先选择版本");
            return;
        }
        const label = platform === "ios" ? "iOS用户数" : "Android用户数";
        await exportUsage(
            {
                page: 1,
                size: 10000,
                versionId: appVersion.id,
                type: appVersion.type,
                platform: platformCode,
                ...defaultSort
            },
            `${label}-${formatLocalTimestamp(new Date())}`
        );
    };

    // 使用 zhihao 表格列模型复现 Vue 用户明细字段和更新时间格式化规则。
    const usageColumns: TableColumnProps<AppVersionUsageRow>[] = [
        {
            label: "个人实名/组织机构",
            minWidth: 160,
            prop: "nameDisplay",
            render: ({ row }) => row.nstitutionName || row.realName || DEFAULT_VALUE
        },
        { label: "手机号", width: 140, prop: "phone", render: ({ row }) => row.phone || DEFAULT_VALUE },
        {
            label: "更新时间",
            width: 160,
            prop: "updatedTime",
            sortable: true,
            sortOrders: ["ascending", "descending", null],
            render: ({ row }) => formatDate(row.updatedTime || row.updateTime)
        }
    ];

    return (
        <ZhDialog
            destroyOnClose
            open={open}
            title={platform === "ios" ? "IOS用户数明细" : "Android用户数明细"}
            width={900}
            footer={null}
            onCancel={onClose}
        >
            {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="warning" /> : null}
            {appVersion?.id == null || appVersion.type == null ? (
                <Alert message="请先选择版本" role="alert" type="warning" />
            ) : (
                <>
                    <ZhTable<AppVersionUsageRow>
                        data={rows}
                        columns={usageColumns}
                        pagination={false}
                        rowKey={row => `${row.phone || "anonymous"}-${row.updatedTime || row.updateTime || ""}`}
                        onSortChange={sort => {
                            // Vue 将更新时间列映射为 updated_time，并在排序后回到第一页。
                            setSortOrder(
                                sort.order === "ascending" ? "asc" : sort.order === "descending" ? "desc" : undefined
                            );
                            setPage(1);
                        }}
                        isShowResetBtn={false}
                        isShowSearchBtn={false}
                        tableHeader={
                            <div className="admin-table-composed-header">
                                <Button
                                    aria-label="导出用户明细"
                                    className="mb-4"
                                    type="primary"
                                    onClick={() => void onExport()}
                                >
                                    导出
                                </Button>
                            </div>
                        }
                        append={
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    className="mt-4 self-end"
                                    current={page}
                                    pageSize={20}
                                    total={total}
                                    onChange={nextPage => setPage(nextPage)}
                                />
                            </div>
                        }
                    />
                </>
            )}
        </ZhDialog>
    );
}
